"use client";

import "vanilla-cookieconsent/dist/cookieconsent.css";

import * as CookieConsent from "vanilla-cookieconsent";
import { disableReactDevTools } from "@/core-nextv3/util/disableReactDevTools"
import React, { useContext, useEffect, useLayoutEffect } from "react"
import { AUTH_SETTING, CART_SETTING, THEME_SETTING } from "@/setting/setting";
import { ChakraProvider, defaultSystem } from "@chakra-ui/react";
import { Toaster } from "react-hot-toast";
import { NextIntlClientProvider } from "next-intl";
import { usePathname } from "next/navigation";
import { useCore } from "@/core-nextv3/core/core";
import { useGetLoggedAuth } from "@/core-nextv3/auth/user.auth.api";
import { useGetCart } from "@/core-nextv3/cart/cart.use";
import dynamic from "next/dynamic";
import { createContext } from "react";
import { CookiesProvider } from "react-cookie";
import { trackEvent, TrackTypes } from "@/core-nextv3/track/track";
import AnalyticsProvider from "@/component-nextv3/AnalyticsProvider";
import { useSetUTMParameters } from "@/core-nextv3/util/util.use";
import { setUTMCart } from "@/core-nextv3/cart/cart.api";
import { HeaderLayout } from "../Layouts/header.layout";
import { FooterLayout } from "../Layouts/footer.layout";
import { MainLayout } from "../Layouts/main.layout";
import { AppProgressProvider as ProgressProvider } from "@bprogress/next";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
//import ClientSideScrollRestorer from "@/component-nextv3/ClientSideScrollRestorer";

declare const document: any;

const CartDrawer = dynamic(() => import("../CartDrawer").then((mod) => mod.CartDrawer), 
    {
	 ssr : false,
    });

const PopupModal = dynamic(() => import("../Views/mobile/pages/Product/PopupModal").then((mod) => mod.PopupModal), 
    {
	 ssr : false,
    });    

const Search = dynamic(() => import("../SearchMenu").then((mod) => mod.Search), 
    {
	 ssr : false,
    });
    
const WhatsAppIcon = dynamic(() => import("@/component-nextv3/WhatsAppIcon").then((mod) => mod.WhatsAppIcon), 
    {
	 ssr : false,
    });  
    
type AppContextType = {
    accountData: any;
    menuData:any;
    footerData:any;
    topHeaderData:any;
    popupData:any;
    giftData:any;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

const useAppContext = () => 
{
    const context = useContext(AppContext);

    if (!context) 
    {
        throw new Error("useAppContext must be used within an AppProvider");
    }

    return context;
};

const queryClient = new QueryClient({
    defaultOptions : {
        queries : {
            gcTime : 1000 * 60 * 60 * 24, // 24 hours
        },
    },
})

if (typeof window !== "undefined")
{
    const localStoragePersister = createSyncStoragePersister({
        storage : window.localStorage,
    })

    persistQueryClient({
        queryClient,
        persister : localStoragePersister,
    })
}

const AppProvider = ({ 
    accountData, 
    menuData,
    footerData,
    children, 
    topHeaderData, 
    giftData, 
    popupData }: any) => 
{
    //const router 					 = useRouter();
    const pathname 					 = usePathname();
    const { user, setCart, setUser } = useCore();
    //const { isMobile, isClient, isDesktop } = useResponsive();
    
    // useEffect(() => 
    // {
    //     if (isMobile && !isAppMobile)
    //     {
    //         router.push("/mobile/" + pathname);
    //     } 
    //     else if (isDesktop && isAppMobile)
    //     {
    //         router.push("/" + pathname.replace("/mobile/", ""));
    //     }

    // }, [ isClient ])

    // const nonPathsHeader: boolean = ["/colecoes/feed", "/colecoes/[slug]"].includes(
    //     pathname
    // );
    
    // const nonPathsDeskHeader: boolean = ["/colecoes/feed", "/colecoes/[slug]"].includes(pathname);

    // const nonPathsFooter: boolean = ["/colecoes/feed", "/colecoes/[slug]", "/produtos/[slug]", "/checkout"].includes(
    //     pathname
    // );

    // const pathsProgressPage: boolean = ["/produtos/[slug]"].includes(pathname);    

    // const nonPathsFixedFooter: boolean = [
    //   "/login/",
    //   "/colecoes/feed",
    //   "/colecoes/[slug]",
    //   "/checkout",
    // ].includes(pathname);
    // const nonPathsDeskFixedFooter: boolean = [
    //   "/login/",
    //   "/colecoes/feed",
    //   "/colecoes/[slug]",
    // ].includes(pathname);


    useGetLoggedAuth(AUTH_SETTING, (data: any) => 
    {
        setUser(data);
    });

    useGetCart(CART_SETTING, (data: any) => 
    {
        setCart(data);
    });

    useSetUTMParameters((data:any) => 
    {
        setUTMCart(CART_SETTING.merge({
            data : data
        }))
    })  

    useEffect(() => 
    {        
        CookieConsent.run({

            categories : {
                necessary : {
                    enabled  : true,  // this category is enabled by default
                    readOnly : true  // this category cannot be disabled
                },
            },

            guiOptions : {
                consentModal : {
                    layout             : "bar inline",
        			position           : "bottom center",
                    flipButtons        : false,
    				equalWeightButtons : true
                }
            },						

            language : {
                default      : "en",
                translations : {
                    en : {
                        consentModal : {
                            title              : "",
                            description        : THEME_SETTING.cookieConsent,
                            acceptAllBtn       : "Aceitar",
                            acceptNecessaryBtn : "Rejeitar",
                            showPreferencesBtn : ""
                        },
                        preferencesModal : {
                            title              : "Manage cookie preferences",
                            acceptAllBtn       : "Accept all",
                            acceptNecessaryBtn : "Reject all",
                            savePreferencesBtn : "Accept current selection",
                            closeIconLabel     : "Close modal",
                            sections           : [
                                {
                                    title       : "Somebody said ... cookies?",
                                    description : "I want one!"
                                },																
                            ]
                        }
                    }
                }
            }
        });

        disableReactDevTools();

    }, []);	
  
    useLayoutEffect(() => 
    {
      	if (accountData)  
        {						
            trackEvent(TrackTypes.PageView, { pathname : pathname, user });
        }
  
    }, [ pathname ]);

    useEffect(() => 
    {
        const script = document.createElement("script");
        script.src = "https://www.google.com/recaptcha/enterprise.js?render=" + process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }, []);

    return (<>
        
        <AnalyticsProvider account={accountData}/>

        <ProgressProvider 
            height="4px"
            color={"var(--theme-color)"}
            options={{ showSpinner : false }}
            shallowRouting
        >

            <NextIntlClientProvider
                locale={"pt-BR"}
                messages={{}}
                onError={(error) => 
                {
                    if (error.code === "MISSING_MESSAGE") 
                    {
                        // 
                    }
                }}>
                <ChakraProvider value={defaultSystem}>

                    <CookiesProvider defaultSetOptions={{ path : "/" }}>                                
                            
                        <MainLayout topHeaderData={topHeaderData}>
                            <AppContext.Provider value={{ accountData, menuData, footerData, topHeaderData, giftData, popupData }}>                            
                                                                                            
                                <HeaderLayout/>

                                <QueryClientProvider client={queryClient}>
                                    <div className="page">
                                        {children}
                                    </div>
                                </QueryClientProvider>

                                <FooterLayout/>    
                                
                            </AppContext.Provider>
                        </MainLayout>
                        
                        <div className="desktop"><WhatsAppIcon account={accountData} /></div>

                        <CartDrawer giftPage={giftData}/>

                        <Search/>

                        <PopupModal popupPage={popupData}/>

                    </CookiesProvider>

                </ChakraProvider>
                
            </NextIntlClientProvider>	

        </ProgressProvider>	

        <Toaster
            position="top-center"
            reverseOrder={true}
            containerStyle={{
                fontSize : 16,
                height   : "85px"
            }}
            toastOptions={{
                duration : 2500,
            }}
        />

        {/* <Suspense>
            <ClientSideScrollRestorer/>
        </Suspense> */}

    </>);
}

export { 
    AppProvider,
    useAppContext
}